import Vue from 'vue';
import constants from '../../../plugins/constants';
import { storeGet } from '@/store/store-utils';
import { ACTIVITY_LOG_EXPORT, ACTIVITY_LOG_EXPORT_MESSAGES, ACTIVITY_LOG_MESSAGES, activityLogCycleUrl, FC_MENU_NAMES, FC_NAV_ICONS, FC_PROJECT_API } from '../utils/constants';
import { browserTimeZone, normalizeExportHistory } from '../utils/activityLogExport';
import { consumeCycleListLiteHandoff, pickLiteCycleFields, selectLandingCycle } from '../utils/selectLandingCycle';
const { BOT_NAME } = constants;

import tasksModule from './tasks';
import dashboardModule from './dashboard';
import emailNotificationsModule from './emailNotifications';
import tmReconciliationModule from './tmReconciliation';
import transactionMatchingModule from './transactionMatching';
import informationManagerModule from './informationManager';
import trialBalanceModule from './trialBalance';
import projectModule from './project';
import setupModule from './setup';
import accrualsModule from './accruals';
import chartOfAccountModule from './chartOfAccount';

/** Shared by parallel fetchCyclesForSwitcher callers for one client. Not a cache. */
let cyclesForSwitcherInflight = null;
let cyclesForSwitcherInflightClient = null;
/** Bumped when close, delete, or reopen drops the lite list so a late response cannot restore it. */
let cyclesForSwitcherGeneration = 0;

function browserSessionStorage() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch (_) {
    return null;
  }
}

function cyclesForSwitcherCacheValid(state, clientId) {
  return Boolean(clientId)
    && String(state.cyclesForSwitcherClient || '') === String(clientId)
    && Array.isArray(state.cyclesForSwitcher)
    && state.cyclesForSwitcher.length > 0;
}

function stillActiveClient(rootState, clientId) {
  return String(rootState.client || '') === String(clientId || '');
}

async function loadCyclesForSwitcher({ commit, rootState, clientId, isCurrent, generation }) {
  const stillThisLoad = () => generation === cyclesForSwitcherGeneration;
  const storage = browserSessionStorage();
  const handedOff = clientId ? consumeCycleListLiteHandoff(storage, clientId) : null;
  if (handedOff) {
    if (stillActiveClient(rootState, clientId) && stillThisLoad()) {
      commit('setCyclesForSwitcher', { list: handedOff, clientId });
    }
    return handedOff;
  }

  commit('setCyclesForSwitcherLoading', true);
  try {
    const response = await Vue.axios.post(FC_PROJECT_API.GET_V3_LITE);
    if (!stillActiveClient(rootState, clientId) || !stillThisLoad()) return [];
    if (response?.data?.status === 'success') {
      const list = (response.data.data || []).map(pickLiteCycleFields);
      if (!stillThisLoad()) return list;
      commit('setCyclesForSwitcher', { list, clientId: clientId || null });
      return list;
    }
    if (!stillThisLoad()) return [];
    commit('setCyclesForSwitcher', { list: [], clientId: null });
    commit('sbError', response?.data?.message || 'Failed to load cycles', { root: true });
    return [];
  } catch (error) {
    console.error('[financialclose/store]: fetchCyclesForSwitcher error', error);
    if (stillActiveClient(rootState, clientId) && stillThisLoad()) {
      commit('setCyclesForSwitcher', { list: [], clientId: null });
      commit(
        'sbError',
        error?.response?.data?.message || error?.message || 'Failed to load cycles',
        { root: true },
      );
    }
    return [];
  } finally {
    if (isCurrent() && stillThisLoad()) {
      commit('setCyclesForSwitcherLoading', false);
    }
  }
}

function buildCycleItems(id) {
  const items = [
    { name: 'Dashboard', href: `/financialclose/project/dashboard/${id}`, icon: '/img/navicon/fbc-nav/Dashboard.svg' },
    { name: 'Tasklist', href: `/financialclose/project/checklist/${id}`, icon: '/img/navicon/fbc-nav/Tasks.svg' },
    { name: 'Information Requests', href: `/financialclose/project/informationrequests/${id}`, icon: '/img/navicon/fbc-nav/InfoRequest.svg' },
    { name: 'Trial Balance', href: `/financialclose/project/trialbalance/${id}`, icon: '/img/navicon/fbc-nav/TrialBalance.svg', class: 'noTransform' },
    { name: FC_MENU_NAMES.ACCRUALS, href: `/financialclose/automationaccruals/${id}/estimates`, icon: FC_NAV_ICONS.ACCRUALS },
    // Cycle Settings is pinned in FinancialCloseCycleSideNav (not an expandable flyout)
    {
      name: BOT_NAME,
      icon: '/img/navicon/fbc-nav/AskNoaMenu.svg',
      items: [
        { name: 'Transaction Matching', href: `/financialclose/automationtransactionmatching/${id}`, icon: '' },
        { name: 'Risk Management', href: `/financialclose/automation/riskmanagementhome/${id}`, icon: '' },
        { name: 'Lease Schedule', href: `/financialclose/automationlease/${id}`, icon: '' },
        { name: 'Borrowing Schedule', href: `/financialclose/automationborrowingschedule/${id}`, icon: '' },
        { name: 'FS Preparation', href: `/financialclose/manage/financialstatementpreparation/${id}`, icon: '', class: 'noTransform' }
      ]
    }
  ];
  // Deduplicate by href to avoid duplicates if rebuild called multiple times
  const seen = new Set();
  return items.filter(it => { const key = it.href || it.name; if (seen.has(key)) return false; seen.add(key); return true; });
}

export default {
  modules: {
    tasks: tasksModule,
    dashboard: dashboardModule,
    emailNotifications: emailNotificationsModule,
    tmReconciliation: tmReconciliationModule,
    transactionMatching: transactionMatchingModule,
    informationManager: informationManagerModule,
    trialBalance: trialBalanceModule,
    project: projectModule,
    setup: setupModule,
    accruals: accrualsModule,
    chartOfAccount: chartOfAccountModule,
  },
  state: {
    CycleList: [],
    cyclesForSwitcher: [],
    /** Client id the cached cyclesForSwitcher list belongs to. */
    cyclesForSwitcherClient: null,
    cyclesForSwitcherLoading: false,
    /** Activity log rows keyed by resource id (task/checklist _id). */
    activityLogByResourceId: {},
    menu: [
      { name: 'Home', icon: '/img/navicon/Home.svg', href: '/financialclose/home', right: 'financialclose_project' },
      { name: 'Shortcuts', icon: '/img/navicon/Shortcuts.svg', items: [] },
      { name: 'Cycles', icon: '/img/navicon/Cycles.svg', items: [] },
      {
        name: 'Masters',
        icon: '/img/navicon/FCMasters.svg',
        right: 'financialclose_master_checklist',
        items: [
          { name: 'Location', href: '/financialclose/setup/location', right: 'financialclose_master_checklist', icon: '', edit: true },
          { name: 'Chart of Accounts', href: '/financialclose/setup/chartofaccount', right: 'financialclose_master_chartofaccount', icon: '' },
          { name: 'Tasklist', href: '/financialclose/setup/tasklist', right: 'financialclose_master_checklist', icon: '' },
          { name: 'Users', href: '/financialclose/setup/users', right: 'financialclose_master_user_management', icon: '' },
          { name: 'Information Manager', href: '/financialclose/setup/informationmanager', right: 'financialclose_master_informationmanager', icon: '' },
          { name: 'Automations', href: '/financialclose/setup/emailnotifications', right: 'financialclose_notification_management', icon: '' }
        ]
      }
    ]
  },
  mutations: {
    resetCycles(state) {
      state.CycleList = [];
      const cycles = state.menu.find(m => m.name === 'Cycles');
      if (cycles) cycles.items = [];
    },
    setNewList(state, list) {
      if (!Array.isArray(list)) return;
      state.CycleList = list.map(c => ({ name: c?.description, _id: c?._id, items: buildCycleItems(c?._id) }));
      const cycles = state.menu.find(m => m.name === 'Cycles');
      if (cycles) cycles.items = state.CycleList;
    },
    addItemToCycleList(state, item) {
      if (!item || !item._id) return;
      const idx = state.CycleList.findIndex(c => c._id === item._id);
      const cycleObj = { name: item.description, _id: item._id, items: buildCycleItems(item._id) };
      if (idx > -1) {
        // Replace existing cycle with updated data
        state.CycleList.splice(idx, 1, cycleObj);
      } else {
        // Requirement: if any cycles exist already, replace the whole list (do not append)
        state.CycleList = [cycleObj];
      }
      const cycles = state.menu.find(m => m.name === 'Cycles');
      if (cycles) cycles.items = state.CycleList;
    },
    setCyclesForSwitcher(state, payload) {
      const list = Array.isArray(payload) ? payload : payload?.list;
      state.cyclesForSwitcher = Array.isArray(list) ? list : [];
      if (payload && !Array.isArray(payload) && Object.prototype.hasOwnProperty.call(payload, 'clientId')) {
        state.cyclesForSwitcherClient = payload.clientId ? String(payload.clientId) : null;
      }
    },
    clearCyclesForSwitcher(state) {
      state.cyclesForSwitcher = [];
      state.cyclesForSwitcherClient = null;
    },
    setCyclesForSwitcherLoading(state, loading) {
      state.cyclesForSwitcherLoading = Boolean(loading);
    },
    setActivityLog(state, { resourceId, entries }) {
      if (!resourceId) return;
      Vue.set(
        state.activityLogByResourceId,
        String(resourceId),
        Array.isArray(entries) ? entries : [],
      );
    },
  },
    actions:{

      /**
       * Fetch and set cycle menu from route path on page refresh
       * Extracts project ID from the current route and populates the cycle navigation
       * @param {Object} context - Vuex action context
       * @param {Object} params - Action parameters
       * @param {string} params.routePath - Current route path to extract project ID from
       * @returns {Promise<boolean>} - True if cycle was successfully set, false otherwise
       */
      /**
       * Fetch and set cycle menu from route path on page refresh
       * Extracts project ID from the current route and populates the cycle navigation
       * @param {Object} context - Vuex action context
       * @param {Object} params - Action parameters
       * @param {string} params.routePath - Current route path to extract project ID from
       * @returns {Promise<boolean>} - True if cycle was successfully set, false otherwise
       */
      /**
       * Fetch activity log entries for a given resource.
       * byCycle is only the cycles-page feed: GET listByCycle. Every other caller stays on the reference read.
       * @param {Object} context - Vuex action context
       * @param {Object} params - { resourceId, type, consolidated, moduleName, byCycle }
       * @returns {Promise<{ data: Array, unique_type: Array }>}
       */
      async fetchActivityLog({ commit, rootState }, { resourceId, type, consolidated, moduleName, byCycle = false }) {
        try {
          let response;
          if (byCycle) {
            if (!resourceId) {
              throw new Error(ACTIVITY_LOG_MESSAGES.RESOURCE_OR_MODULE_REQUIRED);
            }
            const params = {};
            if (type && type.length > 0) params.type = type;
            response = await storeGet(Vue.axios, rootState, activityLogCycleUrl(resourceId), params);
          } else {
            let url;
            if (consolidated && moduleName) {
              url = `/v2/activitylogs/get?modulename=${encodeURIComponent(moduleName)}`;
            } else if (resourceId) {
              url = `/v2/activitylogs/get/${resourceId}`;
            } else {
              throw new Error(ACTIVITY_LOG_MESSAGES.RESOURCE_OR_MODULE_REQUIRED);
            }
            const payload = type && type.length > 0 ? { data: { type } } : {};
            response = await Vue.axios.post(url, payload);
          }
          if (response.data.status === 'success') {
            const data = response.data.data || [];
            if (resourceId && !consolidated) {
              commit('setActivityLog', { resourceId, entries: data });
            }
            return {
              data,
              unique_type: response.data.unique_type || [],
            };
          } else {
            throw new Error('Error loading activity log');
          }
        } catch (error) {
          console.error('[financialclose/store]: fetchActivityLog error', error);
          commit(
            'sbError',
            error?.response?.data?.message || error?.message || 'Failed to load activity log',
            { root: true },
          );
          throw error;
        }
      },

      /**
       * Start a cycle export. exportType is activity_log or file.
       * POST /v2/export/:exportType → HTTP 202, data.exportId, data.processingStatus "initiated".
       * Activity-log exports send the browser IANA timezone so the file timestamp is local.
       * @param {Object} params
       * @param {string} params.cycleId
       * @param {string} [params.exportType]
       * @returns {Promise<{ exportId: string, processingStatus: string, message: string }>}
       */
      async requestCycleActivityLogExport({ commit }, { cycleId, exportType }) {
        const type = exportType || ACTIVITY_LOG_EXPORT.EXPORT_TYPE_ACTIVITY_LOG;
        const isFileExport = type === ACTIVITY_LOG_EXPORT.EXPORT_TYPE_FILE;
        const startedFallback = isFileExport
          ? ACTIVITY_LOG_EXPORT_MESSAGES.FILE_EXPORT_STARTED
          : ACTIVITY_LOG_EXPORT_MESSAGES.EXPORT_STARTED;
        const failedFallback = isFileExport
          ? ACTIVITY_LOG_EXPORT_MESSAGES.FILE_EXPORT_FAILED
          : ACTIVITY_LOG_EXPORT_MESSAGES.EXPORT_FAILED;
        try {
          if (!cycleId) {
            throw new Error(ACTIVITY_LOG_EXPORT_MESSAGES.CYCLE_REQUIRED);
          }
          const payload = {
            scope: ACTIVITY_LOG_EXPORT.SCOPE_CYCLE,
            cycleId,
          };
          if (!isFileExport) {
            const timezone = browserTimeZone();
            if (timezone) payload.timezone = timezone;
          }
          const response = await Vue.axios.post(`${ACTIVITY_LOG_EXPORT.URL}/${type}`, payload);
          const body = response?.data || {};
          const exportId = body.data?.exportId;
          const accepted = response?.status === 202 || body.code === 202;
          if (!accepted || !exportId) {
            throw new Error(body.message || failedFallback);
          }
          const message = body.message || startedFallback;
          commit('sbSuccess', message, { root: true });
          return {
            exportId,
            processingStatus: body.data?.processingStatus || ACTIVITY_LOG_EXPORT.PROCESSING_STATUS_INITIATED,
            message,
          };
        } catch (error) {
          console.error('[financialclose/store]: requestCycleActivityLogExport error', error);
          commit(
            'sbError',
            error?.response?.data?.message || error?.message || failedFallback,
            { root: true },
          );
          throw error;
        }
      },

      /**
       * Export history for one cycle. Each row carries exportId, queue stage, and the stored file when finished.
       * GET /v2/export?scope=cycle&cycleId=&cid=
       * @param {Object} params
       * @param {string} params.cycleId
       * @param {boolean} [params.silent] - Skip the snackbar (polling).
       * @returns {Promise<Array>}
       */
      async fetchCycleActivityLogExports({ commit, rootState }, { cycleId, silent }) {
        try {
          if (!cycleId) {
            throw new Error(ACTIVITY_LOG_EXPORT_MESSAGES.CYCLE_REQUIRED);
          }
          const response = await storeGet(Vue.axios, rootState, ACTIVITY_LOG_EXPORT.URL, {
            scope: ACTIVITY_LOG_EXPORT.SCOPE_CYCLE,
            cycleId,
          });
          const body = response?.data || {};
          if (body.status && body.status !== 'success') {
            throw new Error(body.message || ACTIVITY_LOG_EXPORT_MESSAGES.HISTORY_FAILED);
          }
          return normalizeExportHistory(body.data);
        } catch (error) {
          console.error('[financialclose/store]: fetchCycleActivityLogExports error', error);
          if (!silent) {
            commit(
              'sbError',
              error?.response?.data?.message || error?.message || ACTIVITY_LOG_EXPORT_MESSAGES.HISTORY_FAILED,
              { root: true },
            );
          }
          throw error;
        }
      },

      /**
       * Ask Supernova for a signed URL for a finished export. Does not download the file bytes.
       * GET /v2/export/:exportId/file?cid= returns JSON { url, fileName }.
       * @param {Object} params
       * @param {string} params.exportId
       * @param {string} [params.fileName]
       * @returns {Promise<{ url: string, fileName: string }>}
       */
      async downloadCycleActivityLogExport({ commit, rootState }, { exportId, fileName }) {
        try {
          if (!exportId) {
            throw new Error(ACTIVITY_LOG_EXPORT_MESSAGES.DOWNLOAD_FAILED);
          }
          const response = await storeGet(
            Vue.axios,
            rootState,
            `${ACTIVITY_LOG_EXPORT.URL}/${exportId}/file`,
            {},
          );
          const body = response?.data || {};
          if (body.status && body.status !== 'success') {
            throw new Error(body.message || ACTIVITY_LOG_EXPORT_MESSAGES.DOWNLOAD_FAILED);
          }
          const url = body.data?.url;
          if (!url) {
            throw new Error(ACTIVITY_LOG_EXPORT_MESSAGES.DOWNLOAD_FAILED);
          }
          const name = body.data?.fileName || fileName || ACTIVITY_LOG_EXPORT.FILE_FALLBACK_NAME;
          return { url, fileName: name };
        } catch (error) {
          console.error('[financialclose/store]: downloadCycleActivityLogExport error', error);
          commit(
            'sbError',
            error?.response?.data?.message || error?.message || ACTIVITY_LOG_EXPORT_MESSAGES.DOWNLOAD_FAILED,
            { root: true },
          );
          throw error;
        }
      },

      /**
       * Lite cycle list for the active client (switcher, landing, TM import, notifications).
       * Cache is valid only for a non-empty list stored for rootState.client.
       * Parallel callers share one in-flight request. force bypasses the cache only.
       * @param {{ force?: boolean }} [payload]
       */
      async fetchCyclesForSwitcher({ commit, state, rootState }, payload) {
        const force = Boolean(payload && payload.force);
        const clientId = rootState.client ? String(rootState.client) : '';
        if (!force && cyclesForSwitcherCacheValid(state, clientId)) {
          return state.cyclesForSwitcher;
        }
        if (cyclesForSwitcherInflight && cyclesForSwitcherInflightClient === clientId) {
          return cyclesForSwitcherInflight;
        }
        const generation = cyclesForSwitcherGeneration;
        const request = loadCyclesForSwitcher({
          commit,
          rootState,
          clientId,
          generation,
          isCurrent: () => cyclesForSwitcherInflight === request,
        }).finally(() => {
          if (cyclesForSwitcherInflight === request) {
            cyclesForSwitcherInflight = null;
            cyclesForSwitcherInflightClient = null;
          }
        });
        cyclesForSwitcherInflight = request;
        cyclesForSwitcherInflightClient = clientId;
        return request;
      },

      /**
       * Drop the shared lite cycle cache after a close, delete, or reopen.
       * The next landing or switcher read refetches. A response already in flight
       * is ignored so it cannot put the old open/closed state back.
       */
      invalidateCyclesForSwitcher({ commit }) {
        cyclesForSwitcherGeneration += 1;
        cyclesForSwitcherInflight = null;
        cyclesForSwitcherInflightClient = null;
        commit('clearCyclesForSwitcher');
        commit('setCyclesForSwitcherLoading', false);
        console.log('[financialclose/store]: invalidateCyclesForSwitcher');
      },

      async fetchAndSetCycleFromRoute({ commit, state, dispatch, getters, rootState }, { routePath }) {
        if (!rootState.client) return false;
        const cycleRoutePatterns = [
          /\/financialclose\/project\/[^/]+\/([a-f0-9]{24})/i,
          /\/financialclose\/automation[^/]*\/([a-f0-9]{24})/i,
          /\/financialclose\/manage\/financialstatementpreparation\/([a-f0-9]{24})/i
        ];

        let projectId = null;
        for (const pattern of cycleRoutePatterns) {
          const match = routePath?.match(pattern);
          if (match && match[1]) {
            projectId = match[1];
            break;
          }
        }

        if (!projectId) return false;

        // Check if this cycle is already loaded
        const existingCycle = state.CycleList.find(c => c._id === projectId);
        if (existingCycle) return true;

        await dispatch('fetchCyclesForSwitcher');
        const liteCycle = getters.cycleById(projectId);
        if (liteCycle && liteCycle._id) {
          commit('addItemToCycleList', {
            _id: liteCycle._id,
            description: liteCycle.description,
          });
          return true;
        }

        try {
          const response = await Vue.axios.post(`/v3/financialclose/analysis/getdetails/${projectId}`);
          
          if (response?.data?.status === 'success' && response?.data?.data) {
            // API returns data as array - access first element
            const dataArray = response.data.data;
            const cycleData = Array.isArray(dataArray) ? dataArray[0] : dataArray;
            const metadata = cycleData?.metadata || cycleData;
            
            // Try to get _id and description from various possible locations
            const projectIdFromResponse = metadata?._id || cycleData?._id || cycleData?.project?._id;
            const description = metadata?.description || cycleData?.description || cycleData?.project?.description || 'Cycle';
            
            if (projectIdFromResponse) {
              commit('addItemToCycleList', {
                _id: projectIdFromResponse,
                description: description
              });
              return true;
            }
          }
          return false;
        } catch (err) {
          console.error('[financialclose/store]: fetchAndSetCycleFromRoute error', err);
          return false;
        }
      },
    },
  getters: {
    getMenu: state => state.menu,
    landingCycle: (state) => selectLandingCycle(state.cyclesForSwitcher),
    cycleById: (state) => (id) => {
      if (!id) return null;
      const key = String(id);
      return (state.cyclesForSwitcher || []).find((cycle) => cycle && String(cycle._id) === key) || null;
    },
    activityLogByResourceId: (state) => (resourceId) => {
      if (!resourceId) return [];
      const entries = state.activityLogByResourceId[String(resourceId)];
      return Array.isArray(entries) ? entries : [];
    },
  }
};
